/**
 * Returns a function, that, when invoked, will only be triggered at most once during
 * a given window of time.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Time window (in milliseconds).
 * @returns {Function}
 */
function throttle(fn, wait = 300) {
  let throttleTimeoutId = -1;
  let tick = false;

  return () => {
    clearTimeout(throttleTimeoutId);
    throttleTimeoutId = setTimeout(fn, wait);

    if (!tick) {
      fn.call();
      tick = true;
      setTimeout(() => {
        tick = false;
      }, wait);
    }
  };
}

if (!customElements.get('sticky-atc')) {
  customElements.whenDefined('product-form').then(() => {
    class StickyAtc extends customElements.get('product-form') {
      constructor() {
        super();

        this.section = this.closest('.section-main-product');
        this.mainProductForm = this.section.querySelector('product-form');
        this.mainProductSubmitForm = this.mainProductForm.getSubmitForm();
        this.submitForm = this.getSubmitForm();
        this.buyButtons = this.section.querySelector('buy-buttons');
        this.imageContainer = this.querySelector('.sticky-atc__image');
        this.variantTitle = this.querySelector('.sticky-atc__details__variant__title');
        this.variantTitle?.toggleAttribute('hidden', !this.section.querySelector('variant-picker'));
        this.button = this.querySelector('.add-to-cart');

        this.boundHandleVariantSelectionChange = this.handleVariantSelectionChange.bind(this);
        this.section.addEventListener('change', this.boundHandleVariantSelectionChange);

        this.throttledOnScroll = throttle(this.handleScroll.bind(this));
        window.addEventListener('scroll', this.throttledOnScroll);

        this.section.addEventListener('on:media-gallery:change', this.updateImage.bind(this));

        this.updateImage();
        this.updateAddToCartButton();
        // Variant changes replace this element with fresh markup that starts
        // with `sticky-atc--out`. Recalculate immediately instead of waiting
        // for the next scroll event to restore the correct visibility.
        this.handleScroll();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.throttledOnScroll);
        this.section.removeEventListener('change', this.boundHandleVariantSelectionChange);
      }

      /**
       * Immediately keeps the sticky form in sync while the variant HTML is loading.
       * @param {object} evt - Change event from the main variant picker.
       */
      handleVariantSelectionChange(evt) {
        const fromListedOption = evt.target.matches('variant-picker .js-option');
        const fromDropdown = evt.target.matches('variant-picker custom-select');
        if (!fromListedOption && !fromDropdown) return;

        this.currentVariantId = evt.target.dataset.variantId || evt.detail?.variantId || '';
        this.syncVariantId(this.currentVariantId);

        const selectedOptions = this.section.querySelector('variant-picker')?.getSelectedOptions();
        if (this.variantTitle && selectedOptions?.every(Boolean)) {
          this.variantTitle.textContent = selectedOptions.join(' / ');
        }
      }

      /**
       * Syncs the selected variant to the sticky product form.
       * @param {string} variantId - Selected variant id.
       */
      syncVariantId(variantId) {
        const stickyVariantInput = this.submitForm?.querySelector('input[name="id"]');
        if (stickyVariantInput) stickyVariantInput.value = variantId;
        this.dataset.currentVariant = variantId;
      }

      /**
       * Handles submission of the product form.
       * @param {object} evt - Event object.
       */
      async handleSubmit(evt) {
        evt.preventDefault();

        const mainVariantId = this.section.querySelector('.js-product-form input[name="id"]').value;
        const variantId = this.currentVariantId !== undefined
          ? this.currentVariantId
          : mainVariantId;
        if (!variantId) {
          const target = this.section.querySelector('#product-info');
          if (target && target.offsetParent) {
            evt.preventDefault();
            theme.scrollToRevealElement(target);
          }
          return;
        }

        // The sticky form owns a separate variant input. Always sync it before
        // ProductForm builds FormData, even if the fetched variant HTML is pending.
        this.syncVariantId(variantId);

        // Validate main form
        const customFormValid = this.mainProductForm.validate();
        if (!customFormValid || !this.mainProductSubmitForm.reportValidity()) {
          evt.preventDefault();
          if (!customFormValid) {
            theme.scrollToRevealElement(this.mainProductForm);
          } else {
            const input = Array.from(this.mainProductSubmitForm.elements).find(
              (el) => !el.checkValidity()
            );
            setTimeout(() => theme.scrollToRevealElement(input), 100);
          }
          return;
        }

        // Clear copied data
        this.submitForm.querySelectorAll('[data-copied]').forEach((el) => el.remove());

        // Copy data
        const formData = new FormData(this.mainProductSubmitForm);
        for (const p of formData) {
          if (!this.submitForm.querySelector(`[name="${p[0]}"]`)) {
            const input = document.createElement('input');
            input.name = p[0];
            input.value = p[1];
            input.hidden = true;
            input.setAttribute('data-copied', '');
            this.submitForm.append(input);
          }
        }
        super.handleSubmit(evt);
      }

      /**
       * Determine visibility after scroll.
       */
      handleScroll() {
        const rect = this.buyButtons.getBoundingClientRect();
        // 检查 buyButtons 是否在视口内
        const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;
        // 如果 buyButtons 在视口内，隐藏 sticky-atc；否则显示
        this.classList.toggle('sticky-atc--out', isInViewport);

        document.body.classList.toggle(
          'scrolled-to-bottom',
          window.scrollY + window.innerHeight + 100 > document.body.scrollHeight
        );
      }

      /**
       * Updates the image.
       */
      updateImage() {
        const mainImage = this.section.querySelector('.media-gallery .main-image .slider__item.is-active .product-media img');

        if (mainImage) {
          this.imageContainer.innerHTML = mainImage.outerHTML;
        } else {
          this.imageContainer.textContent = '';
        }
      }

      /**
       * Updates the add to cart button text.
       */
      updateAddToCartButton() {
        // If there is no variant selected, show unavailable-text
        const variantId = this.section.querySelector('.js-product-form input[name="id"]').value;
        if (!variantId) {
          this.button.innerHTML = this.button.dataset.unavailableText;
        }
      }
    }

    customElements.define('sticky-atc', StickyAtc);
  });
}
